import { useState, useEffect, useCallback } from "react";
import { api, fmtUSD } from "@/lib/api";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ShieldAlert, AlertTriangle, TrendingDown, Clock, FileSpreadsheet,
  Settings, Loader2, Info, X,
} from "lucide-react";
import { toast } from "sonner";

const ALERTS = [
  { key: "sin_linea", label: "Sin línea activa", short: "Sin línea", Icon: ShieldAlert, cls: "bg-red-50 border-red-200 text-red-700", dot: "bg-red-600" },
  { key: "excedida", label: "Línea excedida", short: "Excedida", Icon: TrendingDown, cls: "bg-orange-50 border-orange-200 text-orange-700", dot: "bg-orange-600" },
  { key: "provisoria", label: "Línea provisoria", short: "Provisoria", Icon: Clock, cls: "bg-amber-50 border-amber-200 text-amber-700", dot: "bg-amber-500" },
];
const ALERT_META = Object.fromEntries(ALERTS.map((a) => [a.key, a]));
const money = (n) => (n === null || n === undefined ? "—" : fmtUSD(n));
// Tabla: 0 / null / undefined -> null (se muestra "—" gris). Negativos y >0 formatean es-PY.
const mt = (n) => (n === null || n === undefined || Number(n) === 0 ? null : fmtUSD(n));

const CLI_REPS = [
  [/SOCIEDAD DE RESPONSABILIDAD LIMITADA/gi, "S.R.L."],
  [/SOCIEDAD ANONIMA/gi, "S.A."],
  [/AGROPECUARIA/gi, "Agrop."],
  [/COOPERATIVA/gi, "Coop."],
  [/COMERCIO/gi, "Com."],
  [/AGRICOLA/gi, "Agríc."],
  [/INDUSTRIAL/gi, "Ind."],
  [/Y DE SERVICIOS/gi, "y Serv."],
];
function shortCli(c) {
  let s = c || "";
  CLI_REPS.forEach(([re, v]) => { s = s.replace(re, v); });
  return s.replace(/\s+/g, " ").trim();
}
function shortVend(v) {
  if (!v) return "—";
  const parts = String(v).split(",");
  const ape = (parts[0] || "").trim().split(/\s+/)[0] || "";
  const nom = (parts[1] || "").trim().split(/\s+/)[0] || "";
  const t = (s) => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : "");
  return t(ape) + (nom ? ` ${nom[0].toUpperCase()}.` : "");
}
const Dash = () => <span className="text-slate-300">—</span>;

function TipoBadge({ tipo }) {
  if (!tipo) return <span className="text-xs text-slate-400">—</span>;
  const def = tipo === "definitiva";
  return (
    <span data-testid={`tipo-${tipo}`} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${def ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
      {def ? "Definitiva" : "Provisoria"}
    </span>
  );
}

export default function CarteraAlertas({ periodo, vendedorActual = "__all__", vendedores = [], isAdmin = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fVend, setFVend] = useState(vendedorActual || "__all__");
  const [fAlerta, setFAlerta] = useState("__all__");
  const [fTipo, setFTipo] = useState("__all__");
  const [detail, setDetail] = useState(null); // {row, facturas}
  const [cfgOpen, setCfgOpen] = useState(false);
  const [zafra, setZafra] = useState("");
  const [urgent, setUrgent] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cartera/alertas", { params: { periodo, vendedor: fVend } });
      setData(data);
      setZafra(data.config?.zafra_close || "");
      setUrgent(data.config?.urgent_days ?? 7);
    } catch (e) {
      toast.error("No se pudieron cargar las alertas de línea de crédito");
    } finally {
      setLoading(false);
    }
  }, [periodo, fVend]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setFVend(vendedorActual || "__all__"); }, [vendedorActual]);

  const openDetail = async (row) => {
    setDetail({ row, facturas: null });
    try {
      const { data } = await api.get("/cartera/alertas/facturas", { params: { periodo, codigo: row.codigo } });
      setDetail({ row, facturas: data.facturas || [] });
    } catch {
      setDetail({ row, facturas: [] });
    }
  };

  const saveCfg = async () => {
    try {
      const { data } = await api.put("/cartera/alertas/config", { zafra_close: zafra, urgent_days: Number(urgent) });
      toast.success(`Guardado · cierre de zafra ${data.zafra_close} · urgente ${data.urgent_days} días`);
      setCfgOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la configuración");
    }
  };

  const rows = (data?.clientes || []).filter(
    (r) => r.alerta !== "vencida" && r.alerta !== "no_vigente"
      && (fAlerta === "__all__" || r.alerta === fAlerta) && (fTipo === "__all__" || (r.tipo_linea || "sin") === fTipo)
  );

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map((r) => ({
      Código: r.codigo, Cliente: r.cliente, Vendedor: r.vendedor || "",
      "Tipo de línea": r.tipo_linea || "sin línea",
      "Cartera abierta (USD)": r.cartera_abierta, "Vencido (USD)": r.vencido,
      "Línea (USD)": r.linea ?? "", "Disponible neto (USD)": r.disponible_neto ?? "",
      "Vence línea": r.fecha_vencimiento || "", "Días restantes": r.dias_restantes ?? "",
      Alerta: r.alerta_label, "Alertas secundarias": (r.secundarias || []).join(" · "),
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Alertas");
    XLSX.writeFile(wb, `Alertas_Linea_Credito_${periodo}.xlsx`);
  };

  const tot = data?.resumen?._total;

  return (
    <section data-testid="cartera-alertas" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-lg font-bold text-emerald-950">Alertas de línea de crédito</h3>
          <p className="text-xs text-slate-500">Cruce de las dos cargas del mes (2.1 Líneas × 2.2 Cuentas) por código de cliente.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="alertas-export-excel" onClick={exportExcel} variant="outline" className="h-9 border-emerald-200 text-emerald-800 hover:bg-emerald-50" disabled={!rows.length}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Exportar a Excel
          </Button>
          {isAdmin && (
            <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
              <DialogTrigger asChild>
                <Button data-testid="alertas-config-btn" variant="outline" className="h-9"><Settings className="h-4 w-4 mr-1.5" /> Ajustes</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Ajustes de zafra y alertas</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Fecha de cierre de zafra (define línea Definitiva)</label>
                    <Input type="date" data-testid="cfg-zafra" value={zafra} onChange={(e) => setZafra(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Días para marcar provisoria como urgente</label>
                    <Input type="number" min="0" data-testid="cfg-urgent" value={urgent} onChange={(e) => setUrgent(e.target.value)} className="mt-1" />
                  </div>
                  <Button data-testid="cfg-save" onClick={saveCfg} className="w-full bg-emerald-700 hover:bg-emerald-800">Guardar</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Avisos de carga faltante */}
      {data && (!data.lineas_cargadas || !data.cuentas_cargadas) && (
        <div data-testid="alertas-faltante" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {!data.lineas_cargadas && !data.cuentas_cargadas && "Faltan ambas cargas del mes: subí el Listado de Líneas (2.1) y el de Cuentas (2.2)."}
          {data.lineas_cargadas && !data.cuentas_cargadas && "Falta cargar el Listado de Cuentas a Recibir (2.2) de este mes."}
          {!data.lineas_cargadas && data.cuentas_cargadas && "Falta cargar el Listado de Líneas de Crédito (2.1) de este mes."}
        </div>
      )}

      {/* Tarjetas */}
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {ALERTS.map((a) => {
              const r = data?.resumen?.[a.key] || { clientes: 0, monto: 0 };
              return (
                <button key={a.key} data-testid={`card-${a.key}`} onClick={() => setFAlerta(fAlerta === a.key ? "__all__" : a.key)}
                  className={`text-left rounded-2xl border p-4 transition-shadow hover:shadow-md ${a.cls} ${fAlerta === a.key ? "ring-2 ring-offset-1 ring-current" : ""}`}>
                  <a.Icon className="h-5 w-5 mb-2" />
                  <div className="text-[11px] font-bold uppercase tracking-wide leading-tight">{a.label}</div>
                  <div className="font-extrabold text-lg mt-1 text-slate-900">{r.clientes}</div>
                  <div className="text-xs text-slate-600">{money(r.monto)}</div>
                </button>
              );
            })}
            <div data-testid="card-sin-cobertura" className="rounded-2xl border border-slate-300 bg-white p-4">
              <Info className="h-5 w-5 mb-2 text-slate-500" />
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 leading-tight">Sin cobertura</div>
              <div className="font-extrabold text-lg mt-1 text-slate-900">{tot ? `${tot.pct_sin_cobertura}%` : "—"}</div>
              <div className="text-xs text-slate-600">{money(tot?.sin_cobertura)}</div>
            </div>
          </div>

          <div className="text-xs text-slate-500" data-testid="alertas-lineas-resumen">
            Líneas: <strong>{tot?.lineas_definitivas ?? 0} definitivas</strong> · <strong>{tot?.lineas_provisorias ?? 0} provisorias</strong>
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-2">
            <Select value={fVend} onValueChange={setFVend}>
              <SelectTrigger data-testid="alertas-f-vendedor" className="h-9 w-48 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los vendedores</SelectItem>
                {vendedores.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fAlerta} onValueChange={setFAlerta}>
              <SelectTrigger data-testid="alertas-f-alerta" className="h-9 w-48 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas las alertas</SelectItem>
                {ALERTS.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fTipo} onValueChange={setFTipo}>
              <SelectTrigger data-testid="alertas-f-tipo" className="h-9 w-44 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todo tipo de línea</SelectItem>
                <SelectItem value="definitiva">Definitiva</SelectItem>
                <SelectItem value="provisoria">Provisoria</SelectItem>
                <SelectItem value="sin">Sin línea</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400 ml-auto">{rows.length} cliente(s)</span>
          </div>

          <p className="text-[11px] text-slate-400 italic" data-testid="alertas-nota">
            El listado de líneas incluye solo líneas activas. "Sin línea activa" puede significar que nunca tuvo, que venció o que está inactiva.
          </p>

          {/* Tabla */}
          <div className="overflow-auto max-h-[70vh] rounded-xl border border-slate-200 bg-white pb-20">
            <table className="w-full text-[12px]" style={{ tableLayout: "fixed" }}>
              <thead className="sticky top-0 z-10 bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Cliente</th>
                  <th className="text-left px-3 py-2 w-[96px]">Vendedor</th>
                  <th className="text-right px-3 py-2 w-[104px]">Cartera abierta</th>
                  <th className="text-right px-3 py-2 w-[96px]">Vencido</th>
                  <th className="text-right px-3 py-2 w-[90px]">Línea</th>
                  <th className="text-right px-3 py-2 w-[100px]">Disp. neto</th>
                  <th className="text-left px-3 py-2 w-[98px]">Vence línea</th>
                  <th className="text-left px-3 py-2 w-[108px]">Alerta</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const meta = ALERT_META[r.alerta];
                  return (
                    <tr key={r.codigo + i} data-testid={`alerta-row-${r.codigo}`} onClick={() => openDetail(r)}
                      className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer h-10">
                      <td className="px-3 py-0">
                        <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
                          <span className="truncate font-medium text-slate-800" style={{ maxWidth: "28ch" }} title={r.cliente}>{shortCli(r.cliente)}</span>
                          <span className="text-[11px] text-slate-400 shrink-0">· {r.codigo}</span>
                        </div>
                      </td>
                      <td className="px-3 py-0 text-slate-600 whitespace-nowrap truncate" title={r.vendedor || ""}>{shortVend(r.vendedor)}</td>
                      <td className="px-3 py-0 text-right tabular-nums whitespace-nowrap">{mt(r.cartera_abierta) ?? <Dash />}</td>
                      <td className={`px-3 py-0 text-right tabular-nums whitespace-nowrap ${r.vencido > 0 ? "text-red-600 font-semibold" : ""}`}>{mt(r.vencido) ?? <Dash />}</td>
                      <td className="px-3 py-0 text-right tabular-nums whitespace-nowrap text-slate-600">{mt(r.linea) ?? <Dash />}</td>
                      <td className={`px-3 py-0 text-right tabular-nums whitespace-nowrap ${r.disponible_neto < 0 ? "text-red-600 font-semibold" : "text-slate-600"}`}>{(r.disponible_neto === null || r.disponible_neto === undefined) ? <Dash /> : fmtUSD(r.disponible_neto)}</td>
                      <td className="px-3 py-0 text-slate-600 whitespace-nowrap">
                        {r.fecha_vencimiento || <Dash />}
                        {r.dias_restantes !== null && r.dias_restantes !== undefined && (
                          <span className={`ml-1 text-[11px] font-semibold ${r.urgente ? "text-red-600" : "text-amber-600"}`}>({r.dias_restantes}d)</span>
                        )}
                      </td>
                      <td className="px-3 py-0">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold border whitespace-nowrap ${meta?.cls} ${r.urgente ? "ring-1 ring-red-400" : ""}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${meta?.dot}`} />{meta?.short || r.alerta_label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Sin alertas para los filtros seleccionados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Detalle de facturas */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detail?.row?.cliente} <span className="text-xs text-slate-400 font-mono">{detail?.row?.codigo}</span>
              <TipoBadge tipo={detail?.row?.tipo_linea} />
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {detail && detail.facturas === null ? (
              <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-700" /></div>
            ) : detail && !detail.facturas.length ? (
              <p className="text-sm text-slate-400 py-4 text-center">Sin facturas detalladas cargadas para este cliente.</p>
            ) : (
              <table className="w-full text-sm" data-testid="detalle-facturas">
                <thead className="text-[11px] uppercase text-slate-400">
                  <tr><th className="text-left py-1">Factura</th><th className="text-left">Venc. Orig.</th><th className="text-right">Valor Orig.</th><th className="text-right">Sal. Correg.</th></tr>
                </thead>
                <tbody>
                  {(detail?.facturas || []).map((f, i) => {
                    const venc = f.venc_orig;
                    const hoy = new Date().toISOString().slice(0, 10);
                    const vencida = venc && venc < hoy;
                    return (
                      <tr key={f.factura + i} className="border-t border-slate-100">
                        <td className="py-1.5 text-slate-700">{f.factura}</td>
                        <td className={vencida ? "text-red-600 font-semibold" : "text-slate-600"}>{venc || "—"}</td>
                        <td className="text-right tabular-nums text-slate-500">{money(f.valor_orig)}</td>
                        <td className="text-right tabular-nums">{money(f.sal_correg)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
